"use client"

import Image from "next/image"
import { Moon, Sun, Activity } from "lucide-react"
import { useTheme } from "next-themes"
import { ConnectionBadge } from "@/components/dashboard/ConnectionBadge"
import { SIEMENS_BLUE } from "@/lib/mprc-config"

interface HeaderProps {
  isConnected: boolean
}

export function Header({ isConnected }: HeaderProps) {
  const { theme, setTheme } = useTheme()

  return (
    <header
      className="sticky top-0 z-50 border-b border-border/50 bg-background/95 backdrop-blur-sm"
      style={{ borderTop: `3px solid ${SIEMENS_BLUE}` }}
    >
      <div className="flex items-center justify-between px-4 py-1.5 gap-4">
        {/* Left: Logo + live badge */}
        <div className="flex items-center gap-3 min-w-0">
          <Image
            src="/logo.png"
            alt="FSM Logo"
            width={72}
            height={36}
            className="object-contain shrink-0"
            priority
          />
          <ConnectionBadge isConnected={isConnected} />
        </div>

        {/* Center: Title */}
        <div className="flex flex-col items-center shrink-0">
          <h1 className="text-base font-bold tracking-widest text-foreground uppercase select-none">
            MPRC Dashboard
          </h1>
          <span className="text-xs text-muted-foreground font-mono tracking-wider">
            Multi-Purpose Robotic Cell
          </span>
        </div>

        {/* Right: Role + theme toggle */}
        <div className="flex items-center gap-3 justify-end">
          <div className="flex items-center gap-1.5">
            <Activity className="size-3.5 text-muted-foreground/70 shrink-0" />
            <span className="text-xs text-muted-foreground">
              Role:{" "}
              <span className="font-semibold" style={{ color: SIEMENS_BLUE }}>
                Supervisor
              </span>
            </span>
          </div>
          <div className="w-px h-4 bg-border/60" />
          <button
            onClick={() => setTheme(theme === "dark" ? "light" : "dark")}
            className="rounded-md p-1.5 text-muted-foreground hover:text-foreground hover:bg-accent transition-colors"
            aria-label="Toggle theme"
          >
            {theme === "dark" ? <Sun className="size-4" /> : <Moon className="size-4" />}
          </button>
        </div>
      </div>
    </header>
  )
}
